import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { FileCopy as FileCopyIcon, OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import * as IfoodService from '../../../services/ifood';
import { useMerchant } from '../../../store/global/useMerchant';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  button: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  codeDisplay: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  stepperRoot: {
    backgroundColor: 'transparent',
  },
  infoBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

function IntegracaoIfood() {
  const merchant = useMerchant(st => st.data.merchant)
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Dados do Step 1
  const [userCode, setUserCode] = useState('');
  const [authorizationCodeVerifier, setAuthorizationCodeVerifier] = useState('');
  const [verificationUrl, setVerificationUrl] = useState('');
  const [expiresIn, setExpiresIn] = useState(0);
  
  // Dados do Step 2
  const [authorizationCode, setAuthorizationCode] = useState('');
  
  // Dados do Step 3
  const storeId = merchant.id || null;
  
  // Status do token
  const [tokenStatus, setTokenStatus] = useState(null);
  const [checkingToken, setCheckingToken] = useState(true);

  // Step 1: Gerar código de usuário
  const handleGenerateUserCode = async () => {
    setLoading(true);
    try {
      const data = await IfoodService.generateUserCode();
      
      setUserCode(data.userCode);
      setAuthorizationCodeVerifier(data.authorizationCodeVerifier);
      setVerificationUrl(data.verificationUrlComplete || data.verificationUrl);
      setExpiresIn(data.expiresIn);
      
      toast.success('Código gerado com sucesso!', { theme: 'colored' });
      setActiveStep(1);
    } catch (error) {
      console.error('Erro ao gerar código:', error);
      toast.error('Erro ao gerar código de usuário. Tente novamente.', { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Abrir portal do iFood
  const handleOpenIfoodPortal = () => {
    if (verificationUrl) {
      window.open(verificationUrl, '_blank');
      toast.info('Cole o código no portal do iFood que acabou de abrir', { theme: 'colored' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado para a área de transferência!', { theme: 'colored' });
  };

  // Step 3: Trocar código de autorização por tokens E salvar (tudo em um)
  const handleExchangeToken = async () => {
    if (!authorizationCode) {
      toast.error('Por favor, insira o código de autorização fornecido pelo iFood', { theme: 'colored' });
      return;
    }

    if (!storeId) {
      toast.error('Store ID não encontrado. Verifique as configurações da loja.', { theme: 'colored' });
      return;
    }

    setLoading(true);
    try {
      const data = await IfoodService.exchangeAuthorizationCode(
        authorizationCode, 
        authorizationCodeVerifier,
        storeId
      );
      
      toast.success('Integração concluída com sucesso!', { theme: 'colored' });
      setActiveStep(2); // Vai direto pro passo final
      checkTokenStatus(); // Revalidar após concluir integração
    } catch (error) {
      console.error('Erro ao concluir integração:', error);
      toast.error('Erro ao concluir integração. Verifique os dados e tente novamente.', { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setUserCode('');
    setAuthorizationCodeVerifier('');
    setVerificationUrl('');
    setAuthorizationCode('');
    checkTokenStatus(); // Revalidar após reset
  };

  // Verificar status do token
  const checkTokenStatus = useCallback(async () => {
    if (!storeId) {
      setCheckingToken(false);
      return;
    }

    setCheckingToken(true);
    try {
      const data = await IfoodService.validateToken(storeId);
      setTokenStatus(data);
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      // Se der erro, assumimos que o token está inválido
      setTokenStatus({
        success: true,
        valid: false,
        authState: 'error',
        error: 'Erro ao verificar token'
      });
    } finally {
      setCheckingToken(false);
    }
  }, [storeId]);

  // Quando merchant.id ainda não veio no primeiro paint, [] impedia nova chamada
  useEffect(() => {
    checkTokenStatus();
  }, [checkTokenStatus]);

  const steps = [
    {
      label: 'Gerar Código de Usuário',
      content: (
        <Box>
          <Typography variant="body2" gutterBottom>
            Clique no botão abaixo para gerar um código de usuário para autenticação no iFood.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateUserCode}
            disabled={loading}
            className={classes.button}
          >
            {loading ? <CircularProgress size={24} /> : 'Gerar Código'}
          </Button>
        </Box>
      ),
    },
    {
      label: 'Autorizar no Portal iFood',
      content: (
        <Box>
          {userCode && (
            <>
              <Alert severity="info" className={classes.infoBox}>
                Este código expira em {Math.floor(expiresIn / 60)} minutos
              </Alert>
              <Typography variant="body2" gutterBottom>
                Use o código abaixo para autorizar no portal do iFood:
              </Typography>
              <div className={classes.codeDisplay}>
                <span>{userCode}</span>
                <Tooltip title="Copiar código">
                  <IconButton size="small" onClick={() => copyToClipboard(userCode)}>
                    <FileCopyIcon />
                  </IconButton>
                </Tooltip>
              </div>
              <Button
                variant="contained"
                color="primary"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenIfoodPortal}
                className={classes.button}
              >
                Abrir Portal iFood
              </Button>
              <Box mt={3}>
                <Typography variant="body2" gutterBottom>
                  Após autorizar no portal do iFood, preencha os dados abaixo:
                </Typography>
                <TextField
                  fullWidth
                  label="Código de Autorização"
                  variant="outlined"
                  value={authorizationCode}
                  onChange={(e) => setAuthorizationCode(e.target.value.toUpperCase())}
                  placeholder="Ex: NFBR-DBMG"
                  margin="normal"
                  inputProps={{
                    style: { textTransform: 'uppercase' }
                  }}
                  helperText="Código fornecido pelo portal do iFood"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleExchangeToken}
                  disabled={loading || !authorizationCode || !storeId}
                  className={classes.button}
                >
                  {loading ? <CircularProgress size={24} /> : 'Concluir Integração'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      ),
    },
    {
      label: 'Concluído',
      content: (
        <Box>
          <Alert severity="success" className={classes.infoBox}>
            Integração com iFood concluída com sucesso!
          </Alert>
          <Typography variant="body2" color="textSecondary" paragraph>
            Os tokens foram salvos e serão renovados automaticamente pelo sistema.
          </Typography>
          <Box mt={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleReset}
              className={classes.button}
            >
              Nova Integração
            </Button>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <div className={classes.root}>
      {/* Alerta de Status do Token */}
      {tokenStatus && (
        <Card className={classes.card}>
          <CardContent>
            {tokenStatus.valid ? (
              <Alert severity="success">
                <Typography variant="h6" gutterBottom>
                  Token do iFood está válido
                </Typography>
                <Typography variant="body2">
                  {tokenStatus.merchantInfo && (
                    <>
                      <strong>Loja:</strong> {tokenStatus.merchantInfo.ifoodName || tokenStatus.ifoodName || 'N/A'}
                      <br />
                    </>
                  )}
                  {tokenStatus.expiresAt && (
                    <>
                      <strong>Expira em:</strong> {new Date(tokenStatus.expiresAt).toLocaleString('pt-BR')}
                      <br />
                    </>
                  )}
                  {tokenStatus.lastRefreshAt && (
                    <>
                      <strong>Última atualização:</strong> {new Date(tokenStatus.lastRefreshAt).toLocaleString('pt-BR')}
                    </>
                  )}
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={checkTokenStatus}
                    disabled={checkingToken}
                    size="small"
                  >
                    Atualizar Status
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Alert severity="warning">
                <Typography variant="h6" gutterBottom>
                  Token do iFood está desatualizado ou inválido
                </Typography>
                <Typography variant="body2" paragraph>
                  {tokenStatus.error || 'O token de integração com o iFood expirou ou está inválido. É necessário renovar a integração.'}
                </Typography>
                {tokenStatus.expiresAt && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    <strong>Expirou em:</strong> {new Date(tokenStatus.expiresAt).toLocaleString('pt-BR')}
                  </Typography>
                )}
                <Typography variant="body2" paragraph>
                  <strong>Para renovar:</strong> Siga os passos abaixo para gerar um novo código e atualizar a integração.
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Importante:</strong> Após concluir a renovação, o token será renovado automaticamente pelo sistema.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {checkingToken && (
        <Card className={classes.card}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography variant="body2">Verificando status do token...</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Mostrar passos apenas se o token não estiver válido */}
      {!checkingToken && (!tokenStatus || !tokenStatus.valid) && (
        <>
          {/* Card de Ajuda */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Como funciona?
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Passo 1:</strong> Gere um código de usuário clicando no botão "Gerar Código".
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Passo 2:</strong> Copie o código gerado, autorize no portal do iFood e preencha os dados solicitados.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Passo 3:</strong> Pronto! A integração está autorizada, está tudo pronto.
              </Typography>
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Integração com iFood
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure a integração com o iFood de forma autônoma.
              </Typography>

              <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepperRoot}>
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                    <StepContent>{step.content}</StepContent>
                  </Step>
                ))}
              </Stepper>

              {activeStep < steps.length - 1 && (
                <Box mt={3} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

    </div>
  );
}

export default IntegracaoIfood;

